import React, { memo } from "react";
import { useSelector } from "react-redux";

import Autocomplete from "@mui/material/Autocomplete";
import ListSubheader from "@mui/material/ListSubheader";
import ListItem from "@mui/material/ListItem";
import TextField from "@mui/material/TextField";
import { ButtonBase, Paper } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { palette } from "../../theme/palette";
import { POSTCODE } from "../../consts/layers";
import { LOCATION, MATRIX_MODES, SEARCH_OPTIONS } from "../../consts/strings";
import { DRIVER_MANAGEMENT, PLAN_MANAGEMENT } from "../../consts/routes";
import { LOCATIONS_PLAN_TYPE } from "../../consts/planTypes";

import {
  matrixModeSelector,
  routeInEditSelector,
  routeObjectsSelector,
  getMatrixProfileType,
} from "../../reducers/matrixProfile/selectors";
import {
  selectedPostcodesSelector,
  selectedCollectionTabSelector,
  markerViewTypeSelector,
} from "../../reducers/ownApp/selectors";

import { WidePaper } from "./WidePaper";
import { StartAdornment } from "./StartAdornment";
import { AutocompleteItem } from "./AutocompleteItem";
import { Listbox } from "./Listbox";

const useStyles = makeStyles({
  listbox: {
    boxSizing: "border-box",
    "& ul": {
      padding: 0,
      margin: 0,
    },
  },
  popupIndicatorOpen: {
    transform: "rotate(0)",
  },
  inputRoot: {
    marginTop: "0.063rem",
  },
  input: {
    textAlign: "left",
    fontSize: "0.75rem",
    color: "white",
    "&::selection": {
      background: palette.typography.white.disabled,
    },
  },
  clearIndicator: {
    color: palette.typography.white.high,
  },
});

const renderGroup = (params) => [
  <ListSubheader key={params.key} component="div">
    {params.group}
  </ListSubheader>,
  params.children,
];

export const VirtualizedAutocomplete = memo(
  ({
    id,
    type,
    setType,
    typeOptions,
    options,
    onChange,
    onEdit,
    application,
  }) => {
    const styles = useStyles();
    const planType = useSelector(getMatrixProfileType);
    const collectionType = useSelector(selectedCollectionTabSelector);
    const showCollectionDefaults = useSelector(markerViewTypeSelector) === "D";
    const matrixMode = useSelector(matrixModeSelector);
    const routeEdit = useSelector(routeInEditSelector);
    const routes = useSelector(routeObjectsSelector);
    const selectedPostcodes = useSelector(selectedPostcodesSelector);
    const hideEditButton =
      ![POSTCODE, LOCATION].includes(type) ||
      ![
        MATRIX_MODES.EDIT_ONE_ROUTE,
        MATRIX_MODES.COLLECTION_TIMES_EDIT,
      ].includes(matrixMode);

    return (
      <Autocomplete
        sentryId={id}
        style={{ width: application === DRIVER_MANAGEMENT ? 300 : 200 }}
        disableListWrap
        onChange={(_, v) => onChange(v)}
        classes={styles}
        forcePopupIcon={false}
        ListboxComponent={Listbox}
        PaperComponent={planType === LOCATIONS_PLAN_TYPE ? WidePaper : Paper}
        renderGroup={renderGroup}
        options={options}
        groupBy={type === POSTCODE ? (option) => option.type : null}
        getOptionLabel={(option) => option.value}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            InputProps={{
              ...params.InputProps,
              startAdornment: application === PLAN_MANAGEMENT && (
                <ButtonBase>
                  <StartAdornment
                    value={type}
                    onChange={setType}
                    options={typeOptions}
                  />
                </ButtonBase>
              ),
            }}
            classes={{
              underline: styles.underline,
              root: styles.inputRoot,
              focused: styles.focused,
            }}
            className={styles.text}
            placeholder={SEARCH_OPTIONS[type]}
          />
        )}
        renderOption={(props, option) => (
          <ListItem {...props}>
            <AutocompleteItem
              id={id}
              type={type}
              option={option}
              matrixMode={matrixMode}
              hideEditButton={hideEditButton}
              planType={planType}
              collectionType={collectionType}
              showCollectionDefaults={showCollectionDefaults}
              routeEdit={routeEdit}
              routes={routes}
              selectedPostcodes={selectedPostcodes}
              onEdit={onEdit}
            />
          </ListItem>
        )}
      />
    );
  }
);
